//@ts-nocheck
import Tabs from "@/components/Tabs/Tabs";
import { useState, useEffect } from "react";
import { Button } from "@/components/Button/Button";
import { useRouter } from "next/router";
import Link from "next/link";

import * as gtag from "@/lib/gtag";

const Extended = ({ data, content }) => {
  const { header = "Header", subheader = "Subheader" } = content;
  const router = useRouter();
  const { step, tab, column } = router.query;

  // Initialize state with URL parameters or defaults
  const [vehicleType, setVehicleType] = useState(step || Object.keys(data)[0]);
  const [configuration, setConfiguration] = useState(
    tab || Object.keys(data[vehicleType])[0]
  );
  const [weightClass, setWeightClass] = useState(column || null);
  const [highlightedColumn, setHighlightedColumn] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);

  // Update state when URL parameters change
  useEffect(() => {
    // Only run on client side and when router is ready
    if (!router.isReady) return;

    if (step) setVehicleType(step);
    if (tab) setConfiguration(tab);
    if (column) {
      setWeightClass(column);
      // Find the column index to highlight
      const weightClasses = data[vehicleType]?.[configuration];
      if (weightClasses) {
        const availableWeightClasses = Object.keys(weightClasses).filter(
          (key) => weightClasses[key]?.data
        );
        const columnIndex = availableWeightClasses.indexOf(column);
        if (columnIndex !== -1) {
          setHighlightedColumn(columnIndex);
        }
      }
    }
  }, [router.isReady, step, tab, column, data, vehicleType, configuration]);

  const handleTabChange = (newVehicleType) => {
    // Get the first available configuration for the new vehicle type
    const firstConfig = Object.keys(data[newVehicleType])[0];

    setVehicleType(newVehicleType);
    if (newVehicleType) {
      gtag.event({
        event: "itemClick",
        itemClick: "Maße und Gewicht",
        firstLevel: `${newVehicleType || ""} `,
        position: "above",
      });
    }

    setConfiguration(firstConfig);
    setWeightClass(null);
    setHighlightedColumn(null);
    // Update URL without page reload
    router.push(
      {
        pathname: router.asPath.split("?")[0], // Use current path without query params
        query: {
          tab: firstConfig,
          step: newVehicleType,
          ...(weightClass && { column: weightClass }),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const handleConfigChange = (newConfig) => {
    gtag.event({
      event: "itemClick",
      itemClick: "Maße und Gewicht",
      firstLevel: `${vehicleType || ""} `,
      secondLevel: `${newConfig || ""} `,
      position: "above",
    });

    setConfiguration(newConfig);
    setWeightClass(null);
    setHighlightedColumn(null);
    // Update URL without page reload
    router.push(
      {
        pathname: router.asPath.split("?")[0],
        query: {
          tab: newConfig,
          step: vehicleType,
          ...(weightClass && { column: weightClass }),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const handleMouseEnter = (index) => {
    setHoveredColumn(index);
  };

  const handleMouseLeave = () => {
    setHoveredColumn(null);
  };

  const renderTable = (weightClasses, config) => {
    console.log("weightClasses", weightClasses);
    if (!weightClasses) return null;

    // Get all weight classes that have data
    const availableWeightClasses = Object.keys(weightClasses).filter(
      (key) => weightClasses[key]?.data
    );

    if (availableWeightClasses.length === 0) return null;

    // Get the first weight class's link data
    const firstWeightClass = availableWeightClasses[0];
    const linkData = weightClasses[firstWeightClass]?.link;

    // Get all unique headers from all weight classes
    const headers = new Set();
    availableWeightClasses.forEach((weightClass) => {
      if (weightClasses[weightClass]?.data) {
        Object.keys(weightClasses[weightClass].data).forEach((key) =>
          headers.add(key)
        );
      }
    });

    const headerArray = Array.from(headers);

    return (
      <div className="flex flex-col gap-8">
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="table-fixed min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="bg-blue-dark text-white p-6 text-left font-medium border-r border-white">
                  Spezifikation
                </th>
                {availableWeightClasses.map((weightClass, colIndex) => (
                  <th
                    key={weightClass}
                    onMouseEnter={() => handleMouseEnter(colIndex)}
                    onMouseLeave={handleMouseLeave}
                    className={`bg-blue-dark text-white p-6 text-center font-medium border-r border-white last:border-r-0 ${
                      highlightedColumn === colIndex
                        ? "bg-blue-light"
                        : hoveredColumn === colIndex
                        ? "bg-blue-light/50"
                        : ""
                    }`}
                  >
                    Nutzlastklasse {weightClass}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {headerArray.map((header, rowIndex) => {
                const isNaNRow = availableWeightClasses.some(
                  (weightClass) =>
                    weightClasses[weightClass]?.data[header] === "type_header"
                );

                if (isNaNRow) {
                  return (
                    <tr key={rowIndex} className="bg-blue-medium text-white">
                      <td
                        colSpan={availableWeightClasses.length + 1}
                        className="text-left font-bold p-6 whitespace-normal"
                      >
                        {header.replace(/_/g, " ")}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={header}
                    className={rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="border-y-2 border-white font-bold bg-gray-100 p-6 !max-w-[250px]">
                      {header.replace(/_/g, " ")}
                    </td>
                    {availableWeightClasses.map((weightClass, colIndex) => (
                      <td
                        key={`${weightClass}-${header}`}
                        onMouseEnter={() => handleMouseEnter(colIndex)}
                        onMouseLeave={handleMouseLeave}
                        className={`border-2 border-gray-100 px-10 text-center text-gray-600 py-2 whitespace ${
                          highlightedColumn === colIndex
                            ? "bg-[#008CBA29]"
                            : hoveredColumn === colIndex
                            ? "bg-[#008CBA29]/50"
                            : ""
                        }`}
                      >
                        {weightClasses[weightClass]?.data[header] || "—"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {linkData && (
          <div className="flex w-full justify-center">
            <Link
              href={`${linkData.href}/?layout=${config}`}
              target={linkData.target}
              className="w-full"
            >
              <Button type="primary" className="w-full !min-w-screen">
                {linkData.linkText} Angebote
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto">
      <div className="flex flex-col items-center justify-center py-12">
        <h1 className="lg:text-7xl text-5xl font-bold text-blue-dark mb-2">
          {header}
        </h1>
        <h2 className="lg:text-5xl text-3xl font-normal text-blue-dark text-center max-w-2xl">
          {subheader}
        </h2>
      </div>

      <div className="mb-8">
        <Tabs.Root
          value={vehicleType}
          onValueChange={handleTabChange}
          className="relative"
        >
          <Tabs.List className="w-full lg:overflow-y-visible overflow-y-scroll flex mb-8">
            <div className="flex lg:justify-center justify-start pb-8">
              {Object.keys(data).map((type) => (
                <Tabs.Trigger
                  value={type}
                  key={type}
                  className="active:text-blue-light hover:text-blue-medium hover:border-b-4 active:border-b-4 active:font-bold text-gray-600 px-8 pt-4 pb-8 border-b-2 border-gray-300 active:border-orange-light hover:border-orange"
                >
                  <span className="whitespace-nowrap">{type}</span>
                </Tabs.Trigger>
              ))}
            </div>
          </Tabs.List>

          {Object.entries(data).map(([type, configs]) => (
            <Tabs.Content key={type} value={type}>
              <Tabs.Root
                value={configuration}
                onValueChange={handleConfigChange}
                className="relative"
              >
                <Tabs.List className="w-full flex mb-8">
                  <div className="flex w-full lg:justify-center items-stretch justify-start gap-4">
                    {Object.keys(configs).map((config) => (
                      <Tabs.Trigger
                        value={config}
                        key={config}
                        className=" p-5 bg-white  w-full rounded-6 text-blue-light border-2 border-blue-light active:text-white font-bold active:bg-blue-light"
                      >
                        <span className="whitespace-nowrap">{config}</span>
                      </Tabs.Trigger>
                    ))}
                  </div>
                </Tabs.List>

                {Object.entries(configs).map(([config, weightClasses]) => (
                  <Tabs.Content key={config} value={config}>
                    {renderTable(weightClasses, config)}
                  </Tabs.Content>
                ))}
              </Tabs.Root>
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>
    </div>
  );
};

export default Extended;
